// platformView — client side of the "platform mode" feature (mig 695).
//
// A platform admin is, by default, SCOPED to their active tenant (no
// cross-tenant RLS bypass). To use the cross-tenant platform-admin screens
// (TenantAudit / Activity) they must explicitly enter "platform mode", which
// sets a flag in their app_metadata (set_platform_view) so the auth hook mints
// the `platform_view` JWT claim that is_platform_admin() now requires.
//
// This is the inverse of the support_view banner (src/components/
// SupportViewBanner.tsx) and reuses its exact refresh-retry-then-reload dance:
// an adopted concurrent refresh can predate the metadata write, so we verify
// the claim actually flipped before reloading.

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { refreshSessionAfterMetadataChange } from '@/lib/authRefresh'
import { readStoredSession } from '@/lib/offlineSession'
import { decodeJwtPayload } from '@/lib/jwt'

// -----------------------------------------------------------------------------
// FEATURE FLAG — keep FALSE in committed code. The platform-mode UI (banner +
// "View all tenants" prompt) and the set_platform_view RPC call are ALL gated
// on this, so with it off the feature is completely inert: nothing renders, the
// RPC is never called, behaviour is exactly as before mig 695.
//
// It is flipped to true ONLY during the hands-on rollout session, in lock-step
// with applying migration 695 — see docs/PLATFORM-VIEW-MODE-RUNBOOK.md.
// LIVE 2026-06-05: migration 695 applied + login-verified; flag flipped on.
// -----------------------------------------------------------------------------
export const PLATFORM_MODE_ENABLED = true

// Routes where a platform admin needs the cross-tenant bypass. The "View all
// tenants" prompt only appears here; everywhere else, tenant-mode is silent.
const PLATFORM_ADMIN_PATHS = ['/audit', '/activity', '/diag']

export function isPlatformAdminPath(pathname: string): boolean {
  return PLATFORM_ADMIN_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
}

/** Decode the platform_view claim out of an access token. Safe on garbage. */
function readPlatformViewClaim(token: string | undefined): boolean {
  return decodeJwtPayload(token)?.platform_view === true
}

/** Decode the support_view claim (mig 435) out of an access token. Safe on garbage. */
function readSupportViewClaim(token: string | undefined): boolean {
  return decodeJwtPayload(token)?.support_view === true
}

/**
 * Live boolean read of one session claim. Re-reads on auth state changes so a
 * post-reload session updates immediately.
 *
 * NEVER call a lock-taking auth method (getSession etc.) inside the
 * onAuthStateChange callback: auth-js notifies subscribers from inside its own
 * auth flows, and this exact subscriber once deadlocked the whole client on
 * every token refresh (docs/audits/IDLE-WAKE-DEADLOCK-ROOTCAUSE-2026-07-02.md).
 * The callback already receives the session; the initial mount reads the
 * stored session straight from localStorage (lock-free).
 */
function useClaimFlag(read: (token: string | undefined) => boolean): boolean {
  const [on, setOn] = useState(false)
  useEffect(() => {
    setOn(read(readStoredSession()?.access_token))
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_e, session) => setOn(read(session?.access_token)))
    return () => subscription.unsubscribe()
  }, [read])
  return on
}

/** One-shot (non-hook) read of the same claim, for code that runs outside a
 *  component — a money step must not run for a platform admin in platform mode. */
export function inPlatformModeNow(): boolean {
  return PLATFORM_MODE_ENABLED && readPlatformViewClaim(readStoredSession()?.access_token)
}

/** usePlatformView — live boolean: is the current session in platform mode? */
export function usePlatformView(): boolean {
  return useClaimFlag(readPlatformViewClaim)
}

/**
 * useSupportView — live boolean: is a platform admin inside a support view
 * (mig 435)? The one reader of the claim: SupportViewBanner's red bar and any
 * page that must go read-only during a support session use this, so the two can
 * never disagree. While it is true, is_platform_admin() is FALSE and the hook
 * mints no tenant_role, so every role-gated RLS write refuses.
 */
export function useSupportView(): boolean {
  return useClaimFlag(readSupportViewClaim)
}

/**
 * Force a token refresh until `satisfied(token)` holds, then reload. Mirrors
 * SupportViewBanner's stop() loop: an adopted concurrent refresh can hand back a
 * JWT minted BEFORE the metadata/DB write, so we retry and VERIFY the claim
 * reached the wanted state before reloading. Shared by every admin-mode toggle
 * (platform-view enter/exit AND support-view enter) so the refresh dance lives
 * in exactly one place.
 */
async function refreshUntilClaim(
  satisfied: (token: string | undefined) => boolean,
  landing?: string,
): Promise<void> {
  // Refresh through the shared single-flight coordinator (authRefresh.ts), NOT
  // supabase.auth.refreshSession(). The old loop fired refreshSession() up to
  // SIX times, each rotating the one-time refresh_token; interleaved with the
  // watchdog / tenant switcher / auth-js it replayed a consumed token and
  // revoked live sessions (2026-06-15). Each iteration here is single-flight and
  // rotation-guarded, so sequential retries are safe; we still loop a few times
  // because an adopted (concurrent) refresh may predate the write and not yet
  // carry the claim.
  for (let attempt = 0; attempt < 4; attempt++) {
    const r = await refreshSessionAfterMetadataChange()
    if (r.ok && satisfied(r.session.access_token)) break
    if (!r.ok && r.reason === 'revoked') break   // session gone — reload lands on /login
    await new Promise(res => setTimeout(res, 200 + attempt * 100))
  }
  // `landing` replaces the URL instead of reloading it — for a page whose own
  // URL STARTS the switch (/support-view?tenant=…), where a reload would run it
  // again.
  if (landing) window.location.replace(landing)
  else window.location.reload()
}

/** Enter platform mode: cross-tenant bypass on. */
export async function enterPlatformMode(): Promise<void> {
  const { error } = await supabase.rpc('set_platform_view', { p_on: true })
  if (error) throw error
  await refreshUntilClaim(t => readPlatformViewClaim(t) === true)
}

/** Exit platform mode: back to tenant-scoped. */
export async function exitPlatformMode(): Promise<void> {
  const { error } = await supabase.rpc('set_platform_view', { p_on: false })
  if (error) throw error
  await refreshUntilClaim(t => readPlatformViewClaim(t) === false)
}

/**
 * Enter a SUPPORT view of `tenantId`: start a time-boxed support_views session
 * (mig 435), then refresh until the JWT carries the support_view claim (minted
 * by the auth hook, mig 710) and reload. From that point the admin is scoped to
 * the target tenant AS that tenant — is_platform_admin() is FALSE, RLS + the
 * mig-593 write-lock apply, and SupportViewBanner shows the red bar + Stop.
 * The inverse (Stop) already lives in SupportViewBanner.
 */
export async function enterSupportView(tenantId: string, durationMin = 30, landing?: string): Promise<void> {
  const { error } = await supabase.rpc('start_support_view', { p_tenant_id: tenantId, p_duration_min: durationMin })
  if (error) throw error
  await refreshUntilClaim(t => decodeJwtPayload(t)?.support_view === true, landing)
}
